import { htmlUnescape } from '@/lib/utils'
import type { ProductCategory } from '@/services/productCategories'

export type CategoryIndex = {
  byNormalisedName: Map<string, ProductCategory>
  byHash: Map<string, ProductCategory>
}

// PHP snapshots an order's category names lowercased with everything outside [a-zA-Z0-9 ] stripped
// (ep_order_item_product_med_req.php), so tree names are matched in that form.
const normaliseCategoryName = (name: string) => name.toLowerCase().replace(/[^a-z0-9 ]/g, '')

// Snapshot terms arrive lowercased; NHS categories never match the tree, so restore their acronym.
const formatSnapshotCategory = (text: string) =>
  text.replace(/\b[a-z]/g, letter => letter.toUpperCase()).replace(/\bNhs\b/g, 'NHS')

export function buildCategoryIndex(tree: ProductCategory[]): CategoryIndex {
  const byNormalisedName = new Map<string, ProductCategory>()
  const byHash = new Map<string, ProductCategory>()

  for (const category of tree) {
    // WP stores term names HTML-escaped ('&amp;'): the snapshot was taken from that form, the UI shows it decoded.
    const entry = { ...category, name: htmlUnescape(category.name) ?? category.name }
    byHash.set(entry.hash, entry)
    const name = normaliseCategoryName(category.name)
    if (!byNormalisedName.has(name)) byNormalisedName.set(name, entry)
  }

  return { byNormalisedName, byHash }
}

/** The category and its ancestors, root first. */
function lineageOf(category: ProductCategory, byHash: Map<string, ProductCategory>): ProductCategory[] {
  const lineage = [category]
  let parent = category.parentHash ? byHash.get(category.parentHash) : undefined

  while (parent && !lineage.includes(parent)) {
    lineage.unshift(parent)
    parent = parent.parentHash ? byHash.get(parent.parentHash) : undefined
  }

  return lineage
}

/**
 * Root-to-leaf category names of an order, e.g. ['Weight Loss', 'Weight Loss Injectables and Tablets'].
 * The product's current categories come first; `orderedItem.category` is only a snapshot of names
 * taken at order time (no set order, renamed categories go stale), used when the product has left
 * the catalogue. The deepest match is walked up the tree; with no match at all the snapshot is shown
 * as one segment (NHS categories are left out of the tree).
 */
export function getOrderCategoryPath(
  { snapshot, categoryHashes }: { snapshot?: string | null; categoryHashes?: string[] | null },
  index: CategoryIndex | null
): string[] {
  const terms = (snapshot ?? '')
    .split(',')
    .map(term => term.trim())
    .filter(Boolean)

  const fallback = terms.length ? [formatSnapshotCategory(terms.join(', '))] : []
  if (!index) return fallback

  const productCategories = (categoryHashes ?? []).flatMap(hash => index.byHash.get(hash) ?? [])
  const matches = productCategories.length
    ? productCategories
    : terms.flatMap(term => index.byNormalisedName.get(normaliseCategoryName(term)) ?? [])

  if (!matches.length) return fallback

  const lineages = matches.map(category => lineageOf(category, index.byHash))
  const deepest = lineages.reduce((longest, lineage) => (lineage.length > longest.length ? lineage : longest))
  return deepest.map(category => category.name)
}
